// Source of truth for all visible UI strings. English is the source language;
// the Spanish object must keep the same shape (TypeScript will complain if it
// drifts). Tech names (Kafka, Snowflake, etc.) and brand strings (SV4-tech,
// SV4 Ventures, Sergio Píriz) stay the same in both locales.

export const en = {
  nav: {
    services: "Services",
    expertise: "Expertise",
    howWeWork: "How We Work",
    insights: "Insights",
    contact: "Contact",
    bookACall: "Book a call",
    home: "Home",
  },

  hero: {
    pill: "Specialist consultancy · Founder-led · Available for new engagements",
    h1Pre: "Specialist Consultancy for Data Platforms &",
    h1Highlight: "Software Architecture",
    h1Post: ".",
    sub:
      "SV4-tech helps businesses design, build, and scale reliable data platforms, cloud-native systems, and backend architectures — from strategic technical guidance to hands-on implementation.",
    sub2:
      "We work with startups, scaleups, and technical teams that need experienced architecture guidance, reliable engineering delivery, and practical technology decisions.",
    ctaPrimary: "Book a discovery call",
    ctaSecondary: "Explore services",
  },

  clients: {
    trustedBy: "Trusted by",
  },

  services: {
    eyebrow: "Services",
    title: "Where we plug in",
    lede: "Four focused areas where senior input usually has the most impact.",
    items: [
      {
        title: "Data Platform Architecture",
        body:
          "Architecture and implementation of modern data platforms — streaming pipelines, batch processing, data warehouses, lakehouse patterns, and analytics infrastructure.",
      },
      {
        title: "Cloud & Platform Engineering",
        body:
          "Cloud-native architecture, infrastructure design, Kubernetes deployments, CI/CD, observability, and scalable platform foundations.",
      },
      {
        title: "Backend, Integrations & Automation",
        body:
          "Robust backend systems and APIs, plus automation of internal workflows, integrations between systems, and data-driven processes that reduce manual handoffs.",
      },
      {
        title: "Technical Advisory & Architecture Reviews",
        body:
          "Independent technical reviews, roadmap planning, system design, vendor and tooling evaluation, and guidance for complex engineering decisions.",
      },
    ],
  },

  problems: {
    eyebrow: "Problems",
    title: "Common challenges we help solve",
    lede:
      "If any of these sound like your situation, a short conversation usually saves weeks of guessing.",
    items: [
      "Designing a scalable data platform from scratch.",
      "Modernizing fragile or slow data pipelines.",
      "Integrating multiple internal and external systems.",
      "Migrating workloads to cloud-native infrastructure.",
      "Choosing the right architecture before scaling a product.",
      "Improving reliability, observability, and maintainability.",
      "Supporting teams with senior architecture expertise.",
    ],
  },

  howWeWork: {
    eyebrow: "How we work",
    title: "Engagement models",
    lede:
      "We shape the engagement to the problem. Most begin with a short call, then a written scope before any work starts.",
    items: [
      {
        title: "Architecture Review",
        body:
          "A focused assessment of your current systems, risks, and improvement opportunities.",
      },
      {
        title: "Project Delivery",
        body:
          "Hands-on implementation of a defined technical project, platform component, integration, or backend system.",
      },
      {
        title: "Fractional Technical Leadership",
        body:
          "Ongoing senior guidance for teams that need architecture leadership without hiring a full-time CTO or principal engineer.",
      },
      {
        title: "Troubleshooting & Optimization",
        body:
          "Support diagnosing performance, reliability, scalability, integration, or data-quality issues.",
      },
    ],
  },

  engagements: {
    eyebrow: "Example engagements",
    title: "Indicative work we take on",
    lede:
      "A snapshot of past and ongoing engagements. Specifics under NDA available on request.",
    items: [
      {
        tag: "Real-time data",
        text:
          "Designing a real-time ingestion platform using Kafka, Flink, and cloud storage.",
      },
      {
        tag: "Snowflake",
        text: "Reviewing and improving a Snowflake-based analytics architecture.",
      },
      {
        tag: "AWS · Kubernetes",
        text: "Deploying cloud-native applications on AWS and Kubernetes.",
      },
      {
        tag: "Backend · APIs",
        text:
          "Building backend APIs and integrations between internal systems and external platforms.",
      },
      {
        tag: "Pipelines",
        text:
          "Creating reliable data pipelines for reporting, analytics, and operational use cases.",
      },
      {
        tag: "Automation",
        text:
          "Automating internal data-driven workflows and integrations across operational systems.",
      },
      {
        tag: "Pre-scale",
        text:
          "Helping a startup define the right architecture before scaling its engineering team.",
      },
    ],
  },

  expertise: {
    eyebrow: "Expertise",
    title: "Technologies we work with",
    lede: "The toolset we use most often. Picked for fit with the problem, not novelty.",
    groups: {
      data: "Data & streaming",
      cloud: "Cloud & platform",
      backend: "Backend & software",
      analytics: "Analytics & BI",
    },
  },

  founderLed: {
    eyebrow: "Founder-led expertise",
    title: "A senior technical lead, close to the work.",
    p1Pre: "SV4-tech is the consulting practice of",
    p1Mid: ", founder-led by",
    p1Post:
      " — a data platform and software architecture specialist. Clients work directly with a senior technical lead who can shape architecture, guide delivery, and remain close to the implementation.",
    p2:
      "The practice combines strategic architecture thinking with hands-on engineering execution — designed to support data-intensive, cloud-native, and analytics-driven systems without the overhead of a large agency.",
    points: [
      "Senior architecture and delivery experience",
      "Data-intensive and cloud-native systems",
      "Practical communication with technical and business stakeholders",
      "Hands-on implementation, not just advisory",
      "Flexible support for startups, scaleups, and growing technical teams",
    ],
  },

  insightsPreview: {
    eyebrow: "Insights",
    title: "Notes from the work",
    lede:
      "Practical notes on data platforms, cloud architecture, software engineering, and technical delivery.",
    read: "Read",
    viewAll: "View all insights",
  },

  insightsIndex: {
    h1: "Insights",
    description:
      "Short, opinionated pieces on data platforms, cloud architecture, software engineering, and technical delivery — written from the work, not from the marketing.",
    backHome: "Back to home",
    minRead: "min read",
  },

  insightDetail: {
    backToInsights: "All insights",
    next: "Next",
    read: "Read",
    minRead: "min read",
  },

  contact: {
    eyebrow: "Contact",
    title: "Need senior technical support for a data, cloud, or software architecture challenge?",
    lede:
      "SV4-tech can help assess your current architecture, define the right technical approach, and deliver practical implementation support.",
    callCta: "Book a discovery call",
    emailCta: "Send an email",
    labels: {
      email: "Email",
      linkedin: "LinkedIn",
      profile: "Profile",
      freelance: "Freelance",
      based: "Based",
      basedValue: "Madrid · working remote across EU + Americas hours",
    },
    form: {
      name: "Name",
      email: "Business email",
      company: "Company",
      message: "What are you trying to build, fix, or improve?",
      send: "Send message",
      sending: "Sending…",
      ok: "Thanks — we'll be in touch shortly.",
      captchaHint: "Please complete the verification to send your message.",
      errors: {
        emailFree:
          "Please use a business email address — personal email providers (Gmail, Yahoo, Outlook, etc.) aren't accepted.",
        rateLimited: "Too many requests — try again in a minute.",
        personalEmail: "Please use a business email address.",
        invalidInput: "Please check the fields and try again.",
        captchaLoad: "Couldn't load the verification widget. Refresh and try again.",
        sendFailed: "Something went wrong sending the message. Try emailing us directly.",
        network: "Network error — check your connection or email us directly.",
      },
    },
  },

  footer: {
    tagline:
      "SV4-tech is the consulting practice of SV4 Ventures. Specialist consultancy for data platforms, cloud architecture, and software systems — founder-led by Sergio Píriz.",
    rights: "All rights reserved",
  },

  banner: {
    suggestEs: "Esta página también está disponible en español.",
    switchToEs: "Cambiar a español",
    keepEn: "Keep English",
  },

  language: {
    en: "English",
    es: "Español",
    switchTo: "Switch language",
  },
};

export const es: typeof en = {
  nav: {
    services: "Servicios",
    expertise: "Tecnologías",
    howWeWork: "Cómo trabajamos",
    insights: "Insights",
    contact: "Contacto",
    bookACall: "Reservar llamada",
    home: "Inicio",
  },

  hero: {
    pill: "Consultoría especializada · Liderada por su fundador · Disponible para nuevos proyectos",
    h1Pre: "Consultoría especializada en plataformas de datos y",
    h1Highlight: "arquitectura de software",
    h1Post: ".",
    sub:
      "SV4-tech ayuda a empresas a diseñar, construir y escalar plataformas de datos fiables, sistemas cloud-native y arquitecturas de backend — desde la orientación técnica estratégica hasta la implementación práctica.",
    sub2:
      "Trabajamos con startups, scaleups y equipos técnicos que necesitan una guía de arquitectura experimentada, una entrega de ingeniería fiable y decisiones tecnológicas prácticas.",
    ctaPrimary: "Reservar una llamada exploratoria",
    ctaSecondary: "Ver servicios",
  },

  clients: {
    trustedBy: "Empresas que confían en nosotros",
  },

  services: {
    eyebrow: "Servicios",
    title: "En qué intervenimos",
    lede: "Cuatro áreas concretas donde la experiencia senior suele tener mayor impacto.",
    items: [
      {
        title: "Arquitectura de plataformas de datos",
        body:
          "Arquitectura e implementación de plataformas de datos modernas — pipelines de streaming, procesamiento por lotes, data warehouses, patrones lakehouse e infraestructura de analítica.",
      },
      {
        title: "Cloud e ingeniería de plataforma",
        body:
          "Arquitectura cloud-native, diseño de infraestructura, despliegues en Kubernetes, CI/CD, observabilidad y bases de plataforma escalables.",
      },
      {
        title: "Backend, integraciones y automatización",
        body:
          "Sistemas backend robustos y APIs, junto con automatización de flujos internos, integraciones entre sistemas y procesos basados en datos que reducen el trabajo manual.",
      },
      {
        title: "Asesoramiento técnico y revisiones de arquitectura",
        body:
          "Revisiones técnicas independientes, planificación de roadmap, diseño de sistemas, evaluación de proveedores y herramientas, y orientación para decisiones de ingeniería complejas.",
      },
    ],
  },

  problems: {
    eyebrow: "Problemas",
    title: "Retos habituales que ayudamos a resolver",
    lede:
      "Si alguno de estos describe tu situación, una conversación breve suele ahorrar semanas de incertidumbre.",
    items: [
      "Diseñar una plataforma de datos escalable desde cero.",
      "Modernizar pipelines de datos frágiles o lentos.",
      "Integrar múltiples sistemas internos y externos.",
      "Migrar cargas a infraestructura cloud-native.",
      "Elegir la arquitectura adecuada antes de escalar un producto.",
      "Mejorar la fiabilidad, observabilidad y mantenibilidad.",
      "Apoyar a los equipos con experiencia senior en arquitectura.",
    ],
  },

  howWeWork: {
    eyebrow: "Cómo trabajamos",
    title: "Modelos de colaboración",
    lede:
      "Adaptamos el formato al problema. La mayoría empieza con una llamada breve y un alcance escrito antes de empezar a trabajar.",
    items: [
      {
        title: "Revisión de arquitectura",
        body:
          "Una evaluación enfocada de tus sistemas actuales, riesgos y oportunidades de mejora.",
      },
      {
        title: "Entrega de proyecto",
        body:
          "Implementación práctica de un proyecto técnico definido, componente de plataforma, integración o sistema backend.",
      },
      {
        title: "Liderazgo técnico fraccional",
        body:
          "Orientación senior continua para equipos que necesitan liderazgo de arquitectura sin contratar un CTO o ingeniero principal a tiempo completo.",
      },
      {
        title: "Diagnóstico y optimización",
        body:
          "Apoyo para diagnosticar problemas de rendimiento, fiabilidad, escalabilidad, integración o calidad de datos.",
      },
    ],
  },

  engagements: {
    eyebrow: "Ejemplos de proyectos",
    title: "Trabajos representativos",
    lede:
      "Una muestra de proyectos pasados y en curso. Detalles concretos bajo NDA disponibles bajo petición.",
    items: [
      {
        tag: "Datos en tiempo real",
        text:
          "Diseño de una plataforma de ingesta en tiempo real con Kafka, Flink y almacenamiento cloud.",
      },
      {
        tag: "Snowflake",
        text: "Revisión y mejora de una arquitectura analítica basada en Snowflake.",
      },
      {
        tag: "AWS · Kubernetes",
        text: "Despliegue de aplicaciones cloud-native en AWS y Kubernetes.",
      },
      {
        tag: "Backend · APIs",
        text:
          "Desarrollo de APIs backend e integraciones entre sistemas internos y plataformas externas.",
      },
      {
        tag: "Pipelines",
        text:
          "Creación de pipelines de datos fiables para reportes, analítica y casos de uso operativos.",
      },
      {
        tag: "Automatización",
        text:
          "Automatización de flujos internos basados en datos e integraciones entre sistemas operativos.",
      },
      {
        tag: "Pre-escalado",
        text:
          "Acompañamiento a una startup para definir la arquitectura adecuada antes de escalar su equipo de ingeniería.",
      },
    ],
  },

  expertise: {
    eyebrow: "Tecnologías",
    title: "Tecnologías con las que trabajamos",
    lede: "Las herramientas que usamos con más frecuencia. Elegidas por su encaje con el problema, no por novedad.",
    groups: {
      data: "Datos y streaming",
      cloud: "Cloud y plataforma",
      backend: "Backend y software",
      analytics: "Analítica y BI",
    },
  },

  founderLed: {
    eyebrow: "Experiencia liderada por el fundador",
    title: "Un líder técnico senior, cerca del trabajo.",
    p1Pre: "SV4-tech es la práctica de consultoría de",
    p1Mid: ", liderada por su fundador",
    p1Post:
      " — especialista en plataformas de datos y arquitectura de software. Los clientes trabajan directamente con un líder técnico senior que define la arquitectura, guía la entrega y permanece cerca de la implementación.",
    p2:
      "La práctica combina pensamiento de arquitectura estratégica con ejecución práctica de ingeniería — diseñada para apoyar sistemas data-intensive, cloud-native y analíticos sin la sobrecarga de una agencia grande.",
    points: [
      "Experiencia senior en arquitectura y entrega",
      "Sistemas data-intensive y cloud-native",
      "Comunicación práctica con stakeholders técnicos y de negocio",
      "Implementación práctica, no solo asesoramiento",
      "Apoyo flexible para startups, scaleups y equipos técnicos en crecimiento",
    ],
  },

  insightsPreview: {
    eyebrow: "Insights",
    title: "Notas desde el trabajo",
    lede:
      "Notas prácticas sobre plataformas de datos, arquitectura cloud, ingeniería de software y entrega técnica.",
    read: "Leer",
    viewAll: "Ver todos los insights",
  },

  insightsIndex: {
    h1: "Insights",
    description:
      "Piezas breves y con opinión sobre plataformas de datos, arquitectura cloud, ingeniería de software y entrega técnica — escritas desde el trabajo, no desde el marketing. Publicadas en inglés.",
    backHome: "Volver al inicio",
    minRead: "min de lectura",
  },

  insightDetail: {
    backToInsights: "Todos los insights",
    next: "Siguiente",
    read: "Leer",
    minRead: "min de lectura",
  },

  contact: {
    eyebrow: "Contacto",
    title: "¿Necesitas apoyo técnico senior para un reto de datos, cloud o arquitectura de software?",
    lede:
      "SV4-tech puede ayudar a evaluar tu arquitectura actual, definir el enfoque técnico adecuado y entregar apoyo práctico en la implementación.",
    callCta: "Reservar una llamada exploratoria",
    emailCta: "Enviar un email",
    labels: {
      email: "Email",
      linkedin: "LinkedIn",
      profile: "Perfil",
      freelance: "Freelance",
      based: "Ubicación",
      basedValue: "Madrid · trabajo remoto en horarios de Europa y América",
    },
    form: {
      name: "Nombre",
      email: "Email corporativo",
      company: "Empresa",
      message: "¿Qué intentas construir, arreglar o mejorar?",
      send: "Enviar mensaje",
      sending: "Enviando…",
      ok: "Gracias — nos pondremos en contacto pronto.",
      captchaHint: "Por favor, completa la verificación para enviar tu mensaje.",
      errors: {
        emailFree:
          "Por favor, usa un email corporativo — no se aceptan proveedores personales (Gmail, Yahoo, Outlook, etc.).",
        rateLimited: "Demasiadas solicitudes — inténtalo en un minuto.",
        personalEmail: "Por favor, usa un email corporativo.",
        invalidInput: "Por favor, revisa los campos e inténtalo de nuevo.",
        captchaLoad: "No se pudo cargar la verificación. Recarga e inténtalo de nuevo.",
        sendFailed: "Algo salió mal al enviar el mensaje. Envíanos un email directamente.",
        network: "Error de red — comprueba tu conexión o envíanos un email.",
      },
    },
  },

  footer: {
    tagline:
      "SV4-tech es la práctica de consultoría de SV4 Ventures. Consultoría especializada en plataformas de datos, arquitectura cloud y sistemas de software — liderada por su fundador Sergio Píriz.",
    rights: "Todos los derechos reservados",
  },

  banner: {
    suggestEs: "Esta página también está disponible en español.",
    switchToEs: "Cambiar a español",
    keepEn: "Keep English",
  },

  language: {
    en: "English",
    es: "Español",
    switchTo: "Cambiar idioma",
  },
};

export type Translations = typeof en;
